/**
 * Chakra 1 — Muladhara (§6). Element Aarde, kleur rood, mantra LAM (396Hz).
 * Resource-label: Stabiliteit. Wereld: "De Grond Onder Je Voeten".
 *
 * Mechanische data is 1-op-1 uit het spec. Monologen en de drie sub-baas-
 * leermomenten stonden in het spec als "(ongewijzigd uit ontwerp)" en worden in
 * de VN-fase ingevuld (zelf geschreven, ter review).
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra1: Chakra = {
  header: HEADERS[1],
  wereld: 'De Grond Onder Je Voeten',
  codex:
    'Muladhara, "wortelsteun", zit bij het stuitje en de bekkenbodem. Element Aarde, kleur rood, 4 bloemblaadjes, mantra LAM (396Hz). Dit is de chakra van de eerste levensfase: vertrouwen, veiligheid, "ergens bij mogen horen". De levensvraag is: heb ik recht om hier te zijn? Lichamelijk hoort hij bij benen, voeten, wervelkolom, bekkenbodem en bijnieren (stresssysteem). Bij een tekort: angstig, rusteloos, chronisch onveilig, moeite met grenzen en structuur, het lichaam blijft in overlevingsstand. Bij een teveel: star vasthoudend aan zekerheid en materie, weerstand tegen verandering, traagheid. In balans: gegrond, stabiel, aanwezig in je lichaam, vertrouwen dat het leven je draagt. Herstel: gronding, ritme/routine, contact met de aarde, krachtwerk voor het onderlichaam.',

  kaarten: [
    { id: 'ch1-rode-jaspis', naam: 'Rode Jaspis', type: 's', cost: 1, origin: 1, tekst: '+3 stabiliteit, neutraliseert 1 angstaanval', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch1-hematiet', naam: 'Hematiet', type: 's', cost: 1, origin: 1, tekst: 'Bescherm tegen 1 aanval, +2 HP', effect: { full: true, heal: 2 } },
    { id: 'ch1-zwarte-toermalijn', naam: 'Zwarte Toermalijn', type: 's', cost: 2, origin: 1, tekst: 'Verwijder 1 actieve blokkade', effect: { cleanse: 'one' } },
    { id: 'ch1-rookkwarts', naam: 'Rookkwarts', type: 's', cost: 2, origin: 1, tekst: 'Jouw kaarten kosten 1 minder deze beurt', effect: { costCut: 1 } },
    { id: 'ch1-obsidiaan', naam: 'Obsidiaan', type: 's', cost: 3, origin: 1, tekst: 'Baas verliest 1 energie/beurt (3 beurten)', effect: { drainBoss: { amt: 1, turns: 3 } } },
    { id: 'ch1-bijnier-balans', naam: 'Bijnier Balans', type: 'o', cost: 1, origin: 1, tekst: 'Neutraliseert cortisol-aanvallen', effect: { cleanse: 'one' } },
    { id: 'ch1-been-en-bot', naam: 'Been & Bot', type: 'o', cost: 1, origin: 1, tekst: '+3 HP, beschermt 1 beurt', effect: { heal: 3, block: 3 } },
    { id: 'ch1-wervelkolom', naam: 'Wervelkolom', type: 'o', cost: 2, origin: 1, tekst: 'Volgende kaarten +1 effect (2 beurten)', effect: { buffOutput: { amt: 1, turns: 2 } } },
    { id: 'ch1-nierenergie', naam: 'Nierenergie', type: 'o', cost: 2, origin: 1, tekst: '+3 energie volgende beurt', effect: { energyNext: 3 } },
    { id: 'ch1-mountain-pose', naam: 'Mountain Pose', type: 'p', cost: 2, origin: 1, tekst: 'Zolang actief: +2 stabiliteit/beurt', effect: { aura: { block: 2, name: 'Mountain Pose' } } },
    { id: 'ch1-grounding', naam: 'Grounding', type: 'p', cost: 1, origin: 1, tekst: '+4 HP', effect: { heal: 4 } },
    { id: 'ch1-wandelen-in-de-natuur', naam: 'Wandelen in de Natuur', type: 'p', cost: 2, origin: 1, tekst: 'Trek 2 kaarten, +2 energie', effect: { draw: 2, energy: 2 } },
    { id: 'ch1-dagelijkse-routine', naam: 'Dagelijkse Routine', type: 'p', cost: 2, origin: 1, tekst: 'Zolang actief: +1 energie/beurt', effect: { aura: { energy: 1, name: 'Dagelijkse Routine' } } },
    { id: 'ch1-barrevoets-lopen', naam: 'Barrevoets Lopen', type: 'p', cost: 1, origin: 1, tekst: 'Neutraliseert wortelloosheid-aanvallen', effect: { cleanse: 'one' } },
    { id: 'ch1-aarde-element', naam: 'Aarde Element', type: 'e', cost: 3, origin: 1, tekst: 'Alle Muladhara-kaarten ×2 deze beurt', effect: { mult: { origin: 1, amt: 2 } } },
    { id: 'ch1-aardeanker', naam: 'Aardeanker', type: 'e', cost: 2, origin: 1, tekst: 'Blokkeer volgende aanval volledig', effect: { full: true }, meesterschap: true },
    { id: 'ch1-lam-mantra', naam: 'LAM Mantra', type: 'm', cost: 2, origin: 1, tekst: 'Verwijder 1 blokkade, 396Hz toon', effect: { cleanse: 'one', tone: 396 } },
    { id: 'ch1-lam-verdiept', naam: 'LAM Verdiept', type: 'm', cost: 3, origin: 1, tekst: 'Verwijder alle blokkades, +5 HP', effect: { cleanse: 'all', heal: 5, tone: 396 } },
    { id: 'ch1-rood-visualisatie', naam: 'Rood Visualisatie', type: 'k', cost: 1, origin: 1, tekst: '+2 energie, baas -1 schade deze beurt', effect: { energy: 2, weakenBoss: { amt: 1, turns: 1 } } },
    { id: 'ch1-rode-ademhaling', naam: 'Rode Ademhaling', type: 'k', cost: 2, origin: 1, tekst: 'Trek 3 kaarten', effect: { draw: 3 } },
  ],

  bazen: [
    {
      id: 'ch1-varek',
      naam: 'Varek',
      titel: 'De Wurggreep',
      hp: 20,
      energie: 2,
      aanvallen: [
        { naam: 'Nachtelijk Piekeren', cost: 2, effect: { status: { kind: 'energySteal', amt: 2 } } },
        { naam: 'Wurggreep', cost: 1, effect: { status: { kind: 'nullifyAll', turns: 1 } } },
        { naam: 'Cortisolgolf', cost: 3, effect: { dmg: 5 } },
      ],
      zwaktes: [
        { card: 'Rode Jaspis', counters: 'Wurggreep' },
        { card: 'Aarde Element', bonusDmg: 2 },
        { card: 'Dagelijkse Routine', bossEnergyLoss: 1 },
      ],
    },
    {
      id: 'ch1-nihara',
      naam: 'Nihara',
      titel: 'De Leegte Onder Je Voeten',
      hp: 25,
      energie: 2,
      aanvallen: [
        { naam: 'Thuisloosheid', cost: 2, effect: { status: { kind: 'costUp', type: 'p', amt: 1, turns: 2 } } },
        { naam: 'Wortelloosheid', cost: 1, effect: { discard: { n: 1, mode: 'top' } } },
        { naam: 'Verplaatsing', cost: 3, effect: { shuffleHand: true } },
      ],
      zwaktes: [
        { card: 'Mountain Pose', bonusDmg: 4 },
        { card: 'Wandelen in de Natuur', counters: '*' },
        { card: 'Dagelijkse Routine', bonusDmg: 5 },
      ],
    },
    {
      id: 'ch1-durvak',
      naam: 'Durvak',
      titel: 'De Bodemloze',
      hp: 30,
      energie: 3,
      aanvallen: [
        { naam: 'Existentiële Spiraal', cost: 2, effect: { status: { kind: 'lockType', type: 's', turns: 1 } } },
        { naam: 'Het Grote Niets', cost: 3, effect: { energyLoss: 3 } },
        { naam: 'Paralyserende Twijfel', cost: 2, effect: { freezeOne: true } },
      ],
      zwaktes: [
        { card: 'LAM Mantra', counters: 'Existentiële Spiraal' },
        { card: 'Hematiet', counters: 'Het Grote Niets' },
        { type: 'o', bonusDmg: 3 },
      ],
    },
    {
      id: 'ch1-kharos',
      naam: 'Kharos',
      titel: 'De Oerverlammende',
      hp: 50,
      energie: 4,
      eindbaas: true,
      passief:
        'Bouwt de speler in een beurt 0 Block op → +1 blokkade-stack; bij 3 stacks verliest de speler 10 HP en resetten de stacks.',
      aanvallen: [
        { naam: 'Oervrees', cost: 3, effect: { status: { kind: 'costUp', type: 'all', amt: 1, turns: 2 } } },
        { naam: 'Systeemstilstand', cost: 2, effect: { status: { kind: 'lockType', dynamic: 'mostPlayed', turns: 1 } } },
        { naam: 'Totale Bevriezing', cost: 4, effect: { status: { kind: 'skipTurn', turns: 1 } } },
        { naam: 'Wortelafsnijding', cost: 3, effect: { clearAura: true } },
      ],
      combo: { bossHpLoss: 15 },
      zwaktes: [
        { card: 'LAM Mantra', counters: 'Totale Bevriezing' },
        { card: 'Aarde Element', cleanse: 'all', resetStacks: true },
      ],
      unlock: 'ch1-aardeanker',
    },
  ],
};
